import {
  Logger,
  sortTasksByPriorityDescending,
  type TaskData,
  type TaskId,
  type TaskManagementInput,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { taskKey, taskListKey, taskManagementKey } from './keys'

const logger = new Logger('useTaskCache')

logger.level = 'verbose'

export function useTaskCache(): {
  clearList: VoidFunction
  clearManagementList: (props?: TaskManagementInput) => void
  removeItem: (taskId: TaskId) => void
  replaceItem: (task: TaskData) => void
  completeItem: (taskId: TaskId) => void
  progressItem: (taskId: TaskId) => void
  setItem: (task: TaskData) => void
} {
  const queryClient = useQueryClient()
  return {
    clearList: () => {
      logger.verbose('invalidateQueries taskListKey')
      queryClient.invalidateQueries({ queryKey: taskListKey() })
    },
    clearManagementList: () => {
      logger.verbose('invalidateQueries taskManagementKey')
      queryClient.invalidateQueries({ queryKey: taskManagementKey() })
    },
    removeItem: (taskId: TaskId) => {
      logger.verbose(`removeItem ${taskId}`)
      queryClient.setQueryData<TaskData[]>(taskListKey(), (old) => old?.filter((task) => task.taskId !== taskId))
    },
    replaceItem: (task: TaskData) => {
      logger.verbose(`replaceItem ${task.taskId}`)
      queryClient.setQueriesData<TaskData[]>({ queryKey: taskListKey() }, (old) =>
        old?.map((t) => (t.taskId === task.taskId ? task : t)).sort(sortTasksByPriorityDescending)
      )
    },
    completeItem: (taskId: TaskId) => {
      logger.verbose(`completeItem ${taskId}`)
      queryClient.setQueryData<TaskData[]>(taskListKey(), (old) =>
        old?.map((t) => (t.taskId === taskId ? { ...t, status: 'Completed', completedAt: Date.now() } : t))
      )
    },
    progressItem: (taskId: TaskId) => {
      logger.verbose(`progressItem ${taskId}`)
      queryClient.setQueryData<TaskData[]>(taskListKey(), (old) =>
        old?.map((task) => {
          if (task.taskId !== taskId) return task

          const criteria = task.criteria
          if (criteria?.type !== 'activity') return task

          const prevProgress = task.progress ?? 0
          const updatedProgress = prevProgress + 1
          const completed = updatedProgress >= criteria.count
          return { ...task, progress: updatedProgress, status: completed ? 'Completed' : task.status }
        })
      )
    },
    setItem: (task: TaskData) => {
      logger.verbose(`setItem ${task.taskId}`)
      queryClient.setQueryData<TaskData>(taskKey(task.taskId), task)
    },
  }
}
